(() => {
  if (window.monoreePurchaseUIInstalled) return;
  window.monoreePurchaseUIInstalled = true;
  const instances = new WeakMap();
  let counter = 0;
  const setText = (node, value) => { if (node && node.textContent !== value) node.textContent = value; };
  const show = (node, visible) => { if (node && node.hidden === visible) node.hidden = !visible; };

  function state(info) {
    if (['loading', 'error'].includes(info.monoreeVariantState)) return info.monoreeVariantState;
    if (!info.monoreeSizeChosen) return 'pending';
    if (!info.monoreeSelectedVariant?.id) return 'invalid';
    if (!info.monoreeSelectedVariant.available) return 'soldout';
    return info.monoreeCanPurchase() ? 'available' : 'quantity';
  }
  function focusOptions(info) {
    const picker = info.querySelector('[data-monoree-size-option]') || info.querySelector('variant-selects');
    picker?.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    (picker?.querySelector('input:checked, select') || picker?.querySelector('input:not(:disabled)'))?.focus({ preventScroll: true });
  }
  function notificationUrl(ctx, id) {
    const url = new URL(ctx.config.url, location.origin);
    url.searchParams.set('variant', String(id));
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    url.hash = 'monoree-notify';
    return url.href;
  }
  function selectNotification(ctx, id) {
    if (!ctx.config.soldOutVariants.some(variant => String(variant.id) === String(id))) return;
    location.assign(notificationUrl(ctx, id));
  }
  function openChooser(ctx, opener) {
    const variants = ctx.config.soldOutVariants;
    if (!variants.length) return;
    if (variants.length === 1) { selectNotification(ctx, variants[0].id); return; }
    const dialog = document.createElement('dialog');
    dialog.className = 'monoree-restock-chooser';
    const key = `monoree-restock-choice-${++counter}`;
    dialog.setAttribute('aria-labelledby', `${key}-title`);
    const close = document.createElement('button');
    close.type = 'button'; close.className = 'monoree-restock-chooser__close';
    close.textContent = '×'; close.setAttribute('aria-label', ctx.config.labels.close);
    const title = document.createElement('h2'); title.id = `${key}-title`; title.textContent = ctx.config.labels.notify;
    const name = document.createElement('p'); name.textContent = ctx.config.title;
    const label = document.createElement('label'); label.htmlFor = `${key}-select`; label.textContent = ctx.config.labels.chooseOptions;
    const select = document.createElement('select'); select.id = `${key}-select`;
    const placeholder = new Option(ctx.config.labels.chooseOptions, '', true, true); placeholder.disabled = true; select.add(placeholder);
    variants.forEach(variant => select.add(new Option(variant.title, String(variant.id))));
    const next = document.createElement('button'); next.type = 'button'; next.className = 'button button--primary';
    next.textContent = ctx.config.labels.notify; next.disabled = true;
    const finish = () => { dialog.close(); dialog.remove(); opener?.focus({ preventScroll: true }); };
    close.addEventListener('click', finish);
    dialog.addEventListener('cancel', event => { event.preventDefault(); finish(); });
    select.addEventListener('change', () => { next.disabled = !select.value; });
    next.addEventListener('click', () => { const id = select.value; if (id) { finish(); selectNotification(ctx, id); } });
    dialog.append(close, title, name, label, select, next);
    document.body.append(dialog); dialog.showModal(); select.focus();
  }
  function act(ctx) {
    const current = state(ctx.info);
    if (current === 'pending' || current === 'invalid') focusOptions(ctx.info);
    else if (current === 'error') ctx.info.retryMonoreeVariantSelection();
    else if (current === 'soldout' && ctx.config.notifyEnabled) {
      if (ctx.quick) location.assign(notificationUrl(ctx, ctx.info.monoreeSelectedVariant.id));
      else ctx.restock?.click();
    } else if (current === 'available') ctx.submit.click();
  }
  // Keep the last settled view while verifying a selection. Visual continuity
  // never grants permission to buy: ProductInfo remains the authority.
  function setBusy(ctx, loading) {
    const { info, form } = ctx;
    const value = String(loading);
    if (info.dataset.monoreeControlsBusy !== value) info.dataset.monoreeControlsBusy = value;
    if (form.getAttribute('aria-busy') !== value) form.setAttribute('aria-busy', value);
    if (loading && !ctx.loadingTimer && !ctx.waiting) {
      ctx.loadingTimer = setTimeout(() => {
        ctx.loadingTimer = null;
        if (!info.isConnected || state(info) !== 'loading') return;
        ctx.waiting = true;
        info.dataset.monoreeControlsWaiting = 'true';
        setText(ctx.loadingStatus, ctx.config.labels.loading);
      }, 220);
    } else if (!loading) {
      clearTimeout(ctx.loadingTimer);
      ctx.loadingTimer = null;
      ctx.waiting = false;
      if (info.dataset.monoreeControlsWaiting !== 'false') info.dataset.monoreeControlsWaiting = 'false';
      setText(ctx.loadingStatus, '');
    }
  }

  // Animate only a genuine change between purchase and notification modes.
  // The native wallet node is neither cloned nor recreated. Rapid reversals
  // cancel their old animation; an obsolete completion cannot hide a new view.
  function showExpress(ctx, element, visible, animate) {
    let entry = ctx.express.get(element);
    if (!entry) {
      entry = { visible, animation: null };
      ctx.express.set(element, entry);
      show(element, visible);
      return;
    }
    if (entry.visible === visible) return;
    const from = element.hidden ? 0 : element.getBoundingClientRect().height;
    entry.animation?.cancel();
    entry.animation = null;
    entry.visible = visible;
    show(element, true);
    const to = visible ? element.getBoundingClientRect().height : 0;
    if (!animate || !element.animate || matchMedia('(prefers-reduced-motion: reduce)').matches || Math.abs(from - to) < 1) {
      show(element, visible);
      return;
    }
    const animation = element.animate([
      { blockSize: `${from}px`, minBlockSize: '0px', overflow: 'hidden' },
      { blockSize: `${to}px`, minBlockSize: '0px', overflow: 'hidden' },
    ], { duration: 180, easing: 'ease-out', fill: 'both' });
    entry.animation = animation;
    animation.onfinish = () => {
      if (entry.animation !== animation) return;
      show(element, entry.visible);
      entry.animation = null;
      animation.cancel();
    };
  }

  function render(ctx) {
    const { info, form, config, primary, submit, restock, quick } = ctx;
    const current = state(info), labels = config.labels;
    const loading = current === 'loading';
    const busy = Boolean(form.submitInProgress);
    const visual = loading ? ctx.settledState || 'pending' : current;
    const notify = visual === 'soldout' && config.notifyEnabled;
    const initialized = Boolean(ctx.settledState);
    setBusy(ctx, loading);
    if (info.dataset.monoreePurchaseState !== current) info.dataset.monoreePurchaseState = current;
    // Quantity and hint rows also retain their previous layout while loading.
    if (info.dataset.monoreeControlsView !== visual) info.dataset.monoreeControlsView = visual;
    show(submit, visual === 'available' || visual === 'quantity');
    if (loading && submit) {
      if (!submit.disabled) submit.disabled = true;
      setText(submit.querySelector('span'), ctx.submitLabel || labels.add);
    }
    let label = '', locked = false;
    if (visual === 'pending') label = config.allSoldOut ? `${labels.choose} · ${labels.notify}` : labels.choose;
    else if (visual === 'error') label = labels.retry;
    else if (visual === 'invalid') { label = labels.chooseOptions; locked = !info.querySelector('variant-selects'); }
    else if (visual === 'soldout' && (quick || !notify || !restock)) { label = notify ? labels.notify : labels.soldOut; locked = !notify; }
    if (primary) {
      setText(primary.querySelector('span'), label);
      show(primary, Boolean(label));
      const disabled = locked || loading || busy;
      if (primary.disabled !== disabled) primary.disabled = disabled;
    }
    show(restock, Boolean(notify && !quick));
    if (restock) {
      if ((loading || busy) && !restock.disabled) restock.disabled = true;
      if (restock.inert !== (loading || busy)) restock.inert = loading || busy;
    }
    info.querySelectorAll('[data-monoree-accelerated]').forEach(button => {
      const enabled = current === 'available' && !busy;
      const paymentTemplate = enabled && button.querySelector('template[data-monoree-payment-template]');
      if (paymentTemplate) {
        paymentTemplate.replaceWith(paymentTemplate.content);
      }
      if (button.inert !== !enabled) button.inert = !enabled;
      const disabled = String(!enabled);
      if (button.getAttribute('aria-disabled') !== disabled) button.setAttribute('aria-disabled', disabled);
      // Loading does not change the wallet's layout or remount its contents.
      showExpress(ctx, button, visual === 'available', initialized && !loading);
      if (paymentTemplate) window.Shopify?.PaymentButton?.init();
    });
    form.querySelectorAll('[data-monoree-variant-retry]').forEach(button => show(button, false));
    if (!ctx.link && config.notifyEnabled && config.soldOutVariants.length) {
      ctx.link = document.createElement('button'); ctx.link.type = 'button'; ctx.link.className = 'monoree-restock-link';
      ctx.link.setAttribute('data-monoree-restock-choose', ''); ctx.link.setAttribute('aria-haspopup', 'dialog');
      ctx.link.textContent = labels.notify;
      ctx.link.addEventListener('click', () => { if (state(info) !== 'loading' && !form.submitInProgress) openChooser(ctx, ctx.link); });
      form.querySelector('.product-form__buttons')?.after(ctx.link);
    }
    show(ctx.link, !['soldout', 'error'].includes(visual));
    if (ctx.link && ctx.link.disabled !== (loading || busy)) ctx.link.disabled = loading || busy;
    const message = form.querySelector('[data-monoree-variant-message]');
    const appError = Boolean(form.monoreeNotifyFeedback) && current === 'soldout';
    if (!appError && message) {
      const value = visual === 'error' ? labels.error : visual === 'invalid' ? labels.unavailable
        : visual === 'quantity' && !info.monoreeVariantPurchasable ? labels.unavailable : '';
      setText(message, value); show(message, Boolean(value));
    }
    form.querySelectorAll('[data-monoree-size-hint]').forEach(hint => show(hint, visual === 'pending'));
    const sticky = !quick && document.querySelector('[data-monoree-sticky-submit]');
    if (sticky) {
      setText(sticky.querySelector('[data-monoree-sticky-label]'), notify ? labels.notify : label || labels.add);
      const blocked = loading || busy || current === 'quantity' || (current === 'soldout' && !notify);
      if (sticky.disabled !== blocked) sticky.disabled = blocked;
    }
    if (!loading) {
      ctx.settledState = current;
      ctx.submitLabel = submit?.querySelector('span')?.textContent;
      if (!busy) [submit, primary, restock].forEach(button => {
        if (!button || button.hidden) return;
        const style = getComputedStyle(button);
        button.style.setProperty('--monoree-idle-background', style.backgroundColor);
        button.style.setProperty('--monoree-idle-color', style.color);
      });
    }
  }
  function setup(info) {
    if (instances.has(info)) return;
    const form = info.querySelector('product-form');
    let config;
    try { config = JSON.parse(form?.querySelector('[data-monoree-purchase-config]')?.textContent); } catch { return; }
    if (!config?.labels || !Array.isArray(config.soldOutVariants)) return;
    const ctx = { info, form, config, quick: Boolean(info.closest('quick-add-modal')), primary: form.querySelector('[data-monoree-primary-control]'), submit: form.querySelector('[type="submit"]'), restock: form.querySelector('[data-monoree-restock]') };
    ctx.express = new WeakMap();
    ctx.loadingStatus = document.createElement('span');
    ctx.loadingStatus.className = 'visually-hidden';
    ctx.loadingStatus.setAttribute('role', 'status');
    ctx.loadingStatus.setAttribute('aria-live', 'polite');
    form.append(ctx.loadingStatus);
    [ctx.submit, ctx.primary, ctx.restock].forEach(button => {
      if (!button) return;
      const spinner = document.createElement('i');
      spinner.className = 'monoree-control-spinner';
      spinner.setAttribute('aria-hidden', 'true');
      button.append(spinner);
    });
    // Defense in depth for native and programmatic activations while checking.
    const guard = event => {
      if (state(info) !== 'loading') return;
      if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
      if (event.type === 'submit' || event.target.closest?.('.product-form__buttons, [data-monoree-restock-choose]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    info.addEventListener('click', guard, true);
    info.addEventListener('keydown', guard, true);
    info.addEventListener('submit', guard, true);
    instances.set(info, ctx);
    if (ctx.submit?.dataset.monoreePurchasable === 'false' && info.monoreeVariantState === 'ready') {
      info.monoreeVariantPurchasable = false;
      info.applyMonoreeSizeState();
    }
    ctx.primary?.addEventListener('click', () => act(ctx));
    const observer = new MutationObserver(() => { if (info.isConnected) render(ctx); });
    observer.observe(info, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-monoree-variant-state', 'data-monoree-size-pending', 'disabled', 'class'] });
    render(ctx);
    if (!ctx.quick && location.hash === '#monoree-notify' && state(info) === 'soldout') {
      ctx.restock?.scrollIntoView({ block: 'center', behavior: 'instant' });
      ctx.restock?.focus({ preventScroll: true });
    }
  }
  document.addEventListener('product-info:loaded', event => queueMicrotask(() => setup(event.target)));
  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-monoree-sticky-submit]');
    if (!button || button.disabled) return;
    const info = [...document.querySelectorAll('product-info')].find(node => !node.closest('quick-add-modal') && instances.has(node));
    if (info) { event.preventDefault(); event.stopImmediatePropagation(); act(instances.get(info)); }
  }, true);
  Promise.all([customElements.whenDefined('product-info'), customElements.whenDefined('product-form')]).then(() => document.querySelectorAll('product-info').forEach(setup));
})();
